import { useMemo } from 'react'
import { LockIcon } from 'lucide-react'
import { Box, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { Card, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { EmptyState, Header } from 'components'
import { useCanAccess } from 'hooks'

import { ToursPanel } from './components/ToursPanel'
import { AskPanel } from './components/AskPanel'

/**
 * `/internal/analytics/product` (Analytics → Product, VRG-684; was `/internal/usage`, which
 * redirects with its `?tab=`) — PRODUCT-USAGE telemetry for Vera staff: how people are getting on
 * with the help we built.
 *
 * WHY THIS PAGE EXISTS, given both tabs used to live elsewhere. Tours was a tab of
 * `/internal/analytics` and Ask was its own `/internal/ask-stats` row. The split follows
 * from what `/internal/analytics` is becoming: ROI by company and Vera's own sales over
 * time — commercial performance, read by leadership, on a weekly/monthly cadence, and
 * about to grow its own dimensions.
 *
 * Sort the three things on subject, audience and cadence and Tours and Ask land together
 * while ROI stands apart:
 *
 *   ROI / sales   commercial performance   leadership     monthly, trended
 *   Tours         onboarding effectiveness product/design after shipping a tour
 *   Ask           friction / support demand product/support when triaging gaps
 *
 * Leaving Tours in Analytics would have produced a tab bar reading
 * `Overview | ROI | Sales | Tours | Ask` — a container whose label needs an "and", which
 * is the same defect that had Analytics and Ask stats sharing a sidebar group called
 * Operations. Worse in a tab bar than in a nav, because a tab bar has less room to
 * explain itself.
 *
 * TWO TABS, NOT TWO SIDEBAR ROWS, because the internal rail already overflows badly — at
 * 1280x800 its scrollport is 330px against 932px of content. A pairing that is right on
 * the merits and free on rows is the one to take.
 *
 * ONE PERMISSION, `internal_analytics_view` (seeded to platform_admin only), for BOTH tabs
 * since VRG-685. Spencer, 2026-10-08: platform_manager does not see the Analytics section at
 * all, for now. Until then Ask read `internal_support_manage` (platform_admin AND
 * platform_manager) because its log stores verbatim brand questions, and the route took
 * `anyPermission`, so a manager opened this page to Ask alone. That path is removed in the
 * route table and on the API (`ask-stats-internal.controller.ts` → 403 for a manager);
 * `internal_support_manage` still gates the Support page. A per-company "account manager"
 * scoping was proposed and deferred.
 *
 * The tabs still gate themselves on that permission, so the page stays honest if a role is
 * ever given the route without it.
 */

const TAB_ASK = 'ask'
const TAB_TOURS = 'tours'

export const InternalUsagePage = () => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()

  const { isHasAccess: canSeeTours } = useCanAccess({ action: 'internal_analytics_view' })
  // VRG-685 — was `internal_support_manage`; Ask now takes the same grant as Tours.
  const { isHasAccess: canSeeAsk } = useCanAccess({ action: 'internal_analytics_view' })

  const tabs = useMemo(
    () => [
      ...(canSeeTours ? [{ label: 'Tours', value: TAB_TOURS }] : []),
      ...(canSeeAsk ? [{ label: 'Ask', value: TAB_ASK }] : []),
    ],
    [canSeeTours, canSeeAsk],
  )

  /*
    THE TAB IS IN THE URL, which is not decoration: `/internal/ask-stats` redirects here
    as `?tab=ask` (App.tsx) so the links already published in the staff guides and the
    glossary still land on the right tab rather than on whichever one happens to be
    first. It also makes a tab linkable in a support thread.

    Falling back to the first PERMITTED tab rather than a constant matters for the same
    reason the tabs are filtered: hardcoding 'tours' would leave platform_manager on a
    tab that is not in its list.
  */
  const requested = searchParams.get('tab')
  const active = tabs.some((t) => t.value === requested) ? (requested as string) : tabs[0]?.value

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', value)
    // `replace` so tabbing does not stack history entries a user then has to back out of
    // one at a time to leave the page.
    setSearchParams(next, { replace: true })
  }

  // Belt and braces with the route guard, which already requires `internal_analytics_view`,
  // so this should be unreachable. It exists because the alternative to an honest
  // empty state is a page with a Header and a void under it.
  if (tabs.length === 0) {
    return (
      <>
        <Header title="Product" />
        <Card>
          <EmptyState
            icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
            title="You cannot view product analytics"
            text='Product analytics needs the "View analytics" permission. Ask a platform admin to grant it.'
            sx={{ py: 10 }}
          />
        </Card>
      </>
    )
  }

  return (
    <>
      <Header title="Product" />
      {/* Tabs in their own full-width row below the title, not in `Header`'s
          `leftComponents` — see the long note this pattern carries on
          `InternalAnalyticsPage`: the underline row is 52px against `Header`'s 49px
          minHeight, so inline it stretches the title row and takes the title off the
          sidebar-logo datum. */}
      <Tabs redirect={false} tabs={tabs} value={active} onChange={setTab} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }}>
        {active === TAB_TOURS ? <ToursPanel /> : <AskPanel />}
      </Box>
    </>
  )
}
